import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Calendar as CalendarIcon, X } from '@/components/icons'
import { Input } from './ui/input'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'

interface DateInputProps {
  value?: Date
  onChange: (date: Date | undefined) => void
  placeholder?: string
  title?: string
  minDate?: Date
  startMonth?: Date
  endMonth?: Date
  defaultMonth?: Date
  startDate?: Date
  disabled?: boolean
  readOnly?: boolean
  // Show the clear-X while a value is set (PRNTR rows hide it)
  clearable?: boolean
  className?: string
}

// Native `<input type="date">` is used only inside the Capacitor app, where iOS
// shows its native picker. In browsers we open the custom calendar drawer:
// desktop Safari's date input is unusable — keyboard segment edits never commit
// to `.value` (stuck badInput), and empty fields render today's date as
// placeholder so they look filled when they're not.
const isNativePlatform = Capacitor.isNativePlatform()

function NativeDateInput({
  value,
  onChange,
  placeholder,
  minDate,
  endMonth,
  disabled,
  readOnly,
  clearable = true,
  className,
}: DateInputProps) {
  const stringValue = value ? dayjs(value).format('YYYY-MM-DD') : ''
  const minString = minDate ? dayjs(minDate).format('YYYY-MM-DD') : undefined
  const maxString = endMonth ? dayjs(endMonth).endOf('month').format('YYYY-MM-DD') : undefined

  return (
    <Input
      type="date"
      value={stringValue}
      onChange={(e) => {
        const v = e.target.value
        if (!v) {
          onChange(undefined)
          return
        }
        onChange(dayjs(v, 'YYYY-MM-DD').startOf('day').toDate())
      }}
      placeholder={placeholder}
      min={minString}
      max={maxString}
      disabled={disabled}
      readOnly={readOnly}
      className={cn(
        readOnly &&
          'pointer-events-none border-transparent bg-surface-L2 shadow-none',
        className
      )}
      decorationLeft={<CalendarIcon className="w-5 h-5" />}
      decorationRight={
        clearable && !readOnly && !disabled && value ? (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onChange(undefined)
            }}
            className="p-0.5 hover:bg-surface-L2 rounded transition-colors"
            aria-label="Clear date"
          >
            <X className="w-4 h-4" />
          </button>
        ) : undefined
      }
    />
  )
}

function PickerDateInput({
  value,
  onChange,
  placeholder = 'Date',
  minDate,
  startMonth,
  endMonth,
  defaultMonth,
  disabled,
  readOnly,
  clearable = true,
  className,
}: DateInputProps) {
  const [isOpen, setIsOpen] = useState(false)
  const interactive = !disabled && !readOnly

  return (
    <Popover open={isOpen} onOpenChange={(open) => interactive && setIsOpen(open)}>
      <PopoverTrigger asChild>
        <Input
          readOnly
          value={value ? dayjs(value).format('MMM D, YYYY') : ''}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'cursor-pointer',
            readOnly &&
              'pointer-events-none border-transparent bg-surface-L2 shadow-none',
            className
          )}
          decorationLeft={<CalendarIcon className="w-5 h-5" />}
          decorationRight={
            clearable && interactive && value ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(undefined)
                }}
                className="p-0.5 hover:bg-surface-L2 rounded transition-colors"
                aria-label="Clear date"
              >
                <X className="w-4 h-4" />
              </button>
            ) : undefined
          }
        />
      </PopoverTrigger>
      {/* z-[60]: the picker layer — above drawer content at z-[58] (see resizable-drawer). */}
      <PopoverContent className="w-auto p-0 z-[60]" align="start">
        <Calendar
          mode="single"
          selected={value}
          captionLayout="dropdown"
          defaultMonth={value ?? defaultMonth}
          startMonth={startMonth ?? new Date(1920, 0)}
          endMonth={endMonth ?? new Date(2099, 11)}
          disabled={minDate ? { before: minDate } : undefined}
          onSelect={(date) => {
            if (date) onChange(dayjs(date).startOf('day').toDate())
            setIsOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export function DateInput(props: DateInputProps) {
  return isNativePlatform ? <NativeDateInput {...props} /> : <PickerDateInput {...props} />
}
